import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { InputParam, InputParamSE } from "../../types/grid.ts";
import type { ColumnDescription, InitCache, WebAdminPageData } from "../../types/session.ts";
import { getFilter } from "./advancedFilters/registry.ts";
import { DatePresetSelector } from "./advancedFilters/datePresetSelector.tsx";
import { DateFilter } from "./advancedFilters/dateFilter.tsx";
import { DateFilterSimple } from "./advancedFilters/dateFilterSimple.tsx";
import styles from "./scheduleFilterEditor.module.scss";

interface ScheduleFilterEditorProps {
  /** The report's resolved page-data row. Resolved by the parent so the filter
   *  set and the column set always come from the SAME page — a report name
   *  reused across modules must not load one module's filters with another's
   *  columns. */
  pageData: WebAdminPageData | undefined;
  initialFilters: InputParam[];
  cache: InitCache;
  onChange: (filters: InputParamSE[]) => void;
}

interface ResolvedFilter {
  key: string;
  info: ColumnDescription;
  isParameter: boolean;
  isDate: boolean;
  isDateSimple: boolean;
  registration: NonNullable<ReturnType<typeof getFilter>>;
}

/** Parse Params which may be string[] or a comma-separated string. */
function parseParams(raw: unknown): string[] {
  if (typeof raw === "string") return raw.split(",").map(s => s.trim()).filter(Boolean);
  if (Array.isArray(raw)) return raw as string[];
  return [];
}

export function ScheduleFilterEditor({ pageData, initialFilters, cache, onChange }: ScheduleFilterEditorProps) {
  const [filterState, setFilterState] = useState<Record<string, InputParamSE[]>>({});
  const didInit = useRef(false);

  // Build colInfoMap from cache
  const colInfoMap = useMemo(() => {
    const map: Record<string, ColumnDescription> = {};
    for (const col of cache.ColumnInfo ?? []) {
      map[col.ColumnName.toLowerCase()] = col;
    }
    return map;
  }, [cache.ColumnInfo]);

  // Hidden filters set
  const hiddenFiltersSet = useMemo(() => {
    const set = new Set<string>();
    for (const c of pageData?.HiddenFilters ?? []) set.add(c.toLowerCase());
    return set;
  }, [pageData?.HiddenFilters]);

  // Discover filterable columns from PageData only
  const resolved = useMemo(() => {
    const result: ResolvedFilter[] = [];
    const seen = new Set<string>();
    const columns = pageData?.Columns ?? [];
    const params = parseParams(pageData?.Params);

    const isHidden = (lc: string, info?: ColumnDescription | null) => {
      if (hiddenFiltersSet.has(lc)) return true;
      if (!info) return false;
      if (info.Header && hiddenFiltersSet.has(info.Header.toLowerCase())) return true;
      if (info.FilterText && hiddenFiltersSet.has(info.FilterText.toLowerCase())) return true;
      return false;
    };

    const addColumn = (name: string, isParam: boolean) => {
      const stripped = name.startsWith("@") ? name.substring(1) : name;
      const lc = stripped.toLowerCase();
      if (seen.has(lc)) return;
      const info = colInfoMap[lc];
      if (!info?.FilterType) return;
      if (isHidden(lc, info)) return;
      const registration = getFilter(info.FilterType);
      if (!registration) return;
      const ft = info.FilterType.toUpperCase();
      seen.add(lc);
      result.push({ key: lc, info, isParameter: isParam, isDate: ft === "FILTERDATE", isDateSimple: ft === "FILTERDATESIMPLE", registration });
    };

    // 1. Function parameters
    for (const param of params) addColumn(param, true);

    // 2. All columns with any registered filter type
    //    (unlike AdvancedFilterBar which only shows parameterOnly filters,
    //     the schedule editor shows ALL filterable columns including inline types)
    for (const col of columns) addColumn(col, false);

    return result;
  }, [pageData, colInfoMap, hiddenFiltersSet]);

  // Group initial filters by column key (collapsing date suffixes)
  const groupedInitial = useMemo(() => {
    const grouped: Record<string, InputParam[]> = {};
    for (const p of initialFilters) {
      let key = (p.sColumn.startsWith("@") ? p.sColumn.substring(1) : p.sColumn).toLowerCase();
      if (/[01]$/.test(key)) {
        const base = key.slice(0, -1);
        if (colInfoMap[base]?.FilterType) key = base;
      }
      (grouped[key] ??= []).push(p);
    }
    return grouped;
  }, [initialFilters, colInfoMap]);

  // Initialize filter state from initial filters
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    setFilterState(groupedInitial);
  }, [groupedInitial]);

  // Emit all filters on state change
  useEffect(() => {
    const all: InputParamSE[] = [];
    for (const params of Object.values(filterState)) {
      all.push(...params);
    }
    onChange(all);
  }, [filterState, onChange]);

  const handleFilterChange = useCallback((key: string, value: InputParamSE[]) => {
    setFilterState(prev => ({ ...prev, [key]: value }));
  }, []);

  const noop = useCallback(() => {}, []);

  if (resolved.length === 0) {
    return <div className={styles.emptyMessage}>No configurable filters for this report.</div>;
  }

  return (
    <div className={styles.editor}>
      <div className={styles.filterGrid}>
        {resolved.map(({ key, info, isParameter, isDate, isDateSimple, registration }) => {
          const label = info.FilterText || info.Header || info.ColumnName;
          const currentValue = filterState[key] ?? groupedInitial[key] ?? [];

          if (isDate || isDateSimple) {
            return (
              <fieldset key={key} className={styles.filterFieldset}>
                <legend className={styles.filterLegend}>{label}</legend>
                <DatePresetSelector
                  info={info}
                  isParameter={isParameter}
                  value={currentValue}
                  onChange={(v) => handleFilterChange(key, v)}
                  onSearch={noop}
                  cache={cache}
                  FilterComponent={isDateSimple ? DateFilterSimple : DateFilter}
                  isSimple={isDateSimple}
                />
              </fieldset>
            );
          }

          const Component = registration.component;
          return (
            <fieldset key={key} className={styles.filterFieldset}>
              <legend className={styles.filterLegend}>{label}</legend>
              <Component
                info={info}
                isParameter={isParameter}
                value={currentValue}
                onChange={(v: InputParam[]) => handleFilterChange(key, v)}
                onSearch={noop}
                cache={cache}
              />
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}
